import { useCallback, useRef, useState, type ChangeEvent, type InputHTMLAttributes } from "react"
import {
  uploadFilesSequentially,
  type UploadFilesSequentiallyOptions,
  type UploadFilesSequentiallyResult,
} from "@/lib/upload-files-sequentially"

type UseMultiFileUploadArgs = {
  uploadOne: (file: File) => Promise<unknown>
  onBatchStart?: () => void
  onBatchComplete?: (result: UploadFilesSequentiallyResult) => void | Promise<void>
  onError?: (error: unknown) => void
  validateFile?: UploadFilesSequentiallyOptions["validateFile"]
  onFileSkipped?: UploadFilesSequentiallyOptions["onFileSkipped"]
  stopOnError?: boolean
  accept?: string
}

function snapshotFiles(files: FileList | null): File[] {
  return files ? Array.from(files) : []
}

export function useMultiFileUpload({
  uploadOne,
  onBatchStart,
  onBatchComplete,
  onError,
  validateFile,
  onFileSkipped,
  stopOnError,
  accept,
}: UseMultiFileUploadArgs) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFiles = useCallback(
    async (files: FileList | File[] | null) => {
      const batch = Array.isArray(files) ? files : snapshotFiles(files)
      if (batch.length === 0) return

      onBatchStart?.()
      setUploading(true)
      try {
        const result = await uploadFilesSequentially(batch, uploadOne, {
          validateFile,
          onFileSkipped,
          stopOnError,
        })

        if (result.error != null) {
          onError?.(result.error)
        }

        await onBatchComplete?.(result)
      } finally {
        setUploading(false)
        if (fileRef.current) fileRef.current.value = ""
      }
    },
    [uploadOne, onBatchStart, onBatchComplete, onError, validateFile, onFileSkipped, stopOnError],
  )

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const batch = snapshotFiles(e.target.files)
      void handleFiles(batch)
    },
    [handleFiles],
  )

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    type: "file",
    accept,
    multiple: true,
    className: "hidden",
    onChange,
  }

  return { fileRef, uploading, handleFiles, inputProps }
}
